import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { useGetFacilitiesQuery, useGetFacilityByIdQuery } from '@/services/faciltiesApi'
import { FacilityAddOnType } from '@/types/enums'
import { cn } from '@/lib/utils'
import { formatToPKR } from '@/lib/helper'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { FacilitySchedule } from '@/types/facility/schedule'

interface BookingAddOnProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  setValue: UseFormSetValue<FacilitySchedule>;
  trigger?: UseFormTrigger<FacilitySchedule>;
  action: 'add' | 'edit';
  open: boolean;
}

const { VITE_VIEW_S3_URL } = import.meta.env;

const BookingAddOn = ({ control, errors, watch, setValue, trigger, action, open }: BookingAddOnProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const facilityId = watch("general_info.facility_id");
  const scheduleAddOns = watch("add_ons") || [];

  // Get facility details with add-ons
  const { data: facilityDetails, isLoading } = useGetFacilityByIdQuery({ id: facilityId, org_id: orgId, query: "load_add_ons=true" }, {
    skip: !facilityId
  });

  // Fetch all facilities that can be used as add-ons to source their images and names
  const { data: addOnFacilities, isLoading: isLoadingAddOnFacilities } = useGetFacilitiesQuery({
    query: "is_add_on=true",
    orgId,
  });

  // Lightbox state for viewing add-on facility images as a gallery
  const [gallery, setGallery] = useState<{ open: boolean; images: string[]; index: number }>(
    { open: false, images: [], index: 0 }
  );

  const availableAddOns = useMemo(() => {
    return facilityDetails?.add_ons || [];
  }, [facilityDetails]);

  // Track which facility we've initialized add-ons for
  const initializedFacilityRef = useRef<number | null>(null);

  // Initialize add_on_ids with inclusive add-ons when facility changes AND no add-ons are currently selected
  useEffect(() => {
    // Only initialize if we haven't initialized for this facility yet AND no add-ons are currently selected
    if (facilityId && initializedFacilityRef.current !== facilityId && scheduleAddOns.length === 0) {
      if (availableAddOns.length > 0) {
        const initialScheduleAddOns = availableAddOns
          .filter(addOn => addOn.type === FacilityAddOnType.Inclusive)
          .map(addOn => {
            const addOnFacility = addOnFacilities?.data?.find(f => f.id === addOn.add_on_facility_id);
            return {
              id: addOn.id!,
              max_attendees: 1,
              price: addOn.price || 0,
              name: addOnFacility?.name || (addOn as any)?.name || "",
              type: addOn.type,
              add_on_facility_id: addOn.add_on_facility_id!,
            };
          });
        console.log("Auto-selecting inclusive add-ons for new facility:", initialScheduleAddOns);
        setValue("add_ons", initialScheduleAddOns);
        // Mark as initialized only after add-ons have been loaded and applied
        initializedFacilityRef.current = facilityId;
      } else {
        // Mark initialized even if none available to avoid repeated attempts on re-renders
        console.log("No add-ons available for facility; initialized with empty selection");
        initializedFacilityRef.current = facilityId;
      }
    }
  }, [availableAddOns, facilityId, setValue, scheduleAddOns.length, addOnFacilities?.data]);

  // Ensure inclusive add-ons are always present in form (even when editing existing schedules)
  useEffect(() => {
    if (!facilityId || availableAddOns.length === 0) return;
    const inclusiveFromApi = availableAddOns.filter(a => a.type === FacilityAddOnType.Inclusive);
    if (inclusiveFromApi.length === 0) return;

    const missing = inclusiveFromApi.filter(apiAddOn =>
      !scheduleAddOns.some((formAddOn: { id: number | undefined; add_on_facility_id?: number }) =>
        formAddOn.id === apiAddOn.id || formAddOn.add_on_facility_id === apiAddOn.add_on_facility_id
      )
    );

    if (missing.length > 0) {
      const toAdd = missing.map(addOn => {
        const addOnFacility = addOnFacilities?.data?.find(f => f.id === addOn.add_on_facility_id);
        return {
          id: addOn.id!,
          max_attendees: 1,
          price: addOn.price || 0,
          name: addOnFacility?.name || (addOn as any)?.name || "",
          type: addOn.type,
          add_on_facility_id: addOn.add_on_facility_id!,
        };
      });
      const merged = [...scheduleAddOns, ...toAdd];
      console.log("Merging missing inclusive add-ons into form:", { missing: toAdd, previous: scheduleAddOns, merged });
      setValue("add_ons", merged, { shouldDirty: true, shouldValidate: true });
      if (trigger) trigger("add_ons");
    }
  }, [facilityId, availableAddOns, scheduleAddOns, addOnFacilities?.data, setValue, trigger]);

  const handleRemoveAddOn = (addOnIdToRemove: number | undefined) => {
    console.log("Removing add-on:", { addOnIdToRemove, currentSelectedAddOns: scheduleAddOns });
    const updatedScheduleAddOns = scheduleAddOns.filter((addOn: { id: number | undefined; }) => addOn.id !== addOnIdToRemove);
    console.log("Updated schedule add-ons after removal:", updatedScheduleAddOns);
    setValue("add_ons", updatedScheduleAddOns);
    if (trigger) {
      trigger("add_ons");
    }
  };

  const isAddOnSelected = (addOnId: number | undefined, addOnFacilityId?: number) => {
    const isSelected = scheduleAddOns.some((a: { id: number | undefined; add_on_facility_id?: number }) => (
      a.id === addOnId || (addOnFacilityId ? a.add_on_facility_id === addOnFacilityId : false)
    ));
    console.log("Checking if add-on is selected:", { addOnId, addOnFacilityId, scheduleAddOns, isSelected });
    return isSelected;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading add-ons...</div>
          </div>
        </Card>
      </div>
    );
  }

  console.log({ scheduleAddOns, addOnFacilities, facilityDetails, availableAddOns });


  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <p className="font-medium text-base">Add Ons</p>
          <div className="text-sm text-muted-foreground">
            {availableAddOns.length} add-on(s) linked to this facility
          </div>
        </div>
        <Separator className="h-[1px] my-4" />

        {availableAddOns.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-muted-foreground mb-2">
              <i className="fa fa-info-circle text-4xl text-gray-300"></i>
            </div>
            <p className="text-sm text-muted-foreground">
              No add-ons linked with the facility selected
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              You can still proceed to the next step
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {availableAddOns.map((addOn, index) => {
              const selected = isAddOnSelected(addOn.id!, addOn.add_on_facility_id);
              const canRemove = addOn.type === FacilityAddOnType.Exclusive;
              const selectedIndex = scheduleAddOns.findIndex((item: { id: number | undefined; add_on_facility_id?: number }) => (
                item.id === addOn.id || item.add_on_facility_id === addOn.add_on_facility_id
              ));
              return (
                <Card
                  key={addOn.add_on_facility_id}
                  className={cn(
                    "transition-all duration-200",
                    selected
                      ? "border-primary/50 bg-primary/5"
                      : "border-gray-200 bg-gray-50 opacity-70"
                  )}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-3 h-3 rounded-full",
                          selected ? "bg-primary" : "bg-gray-300"
                        )}></div>
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">
                            {addOnFacilities?.data?.find((f) => f.id === addOn.add_on_facility_id)?.name || "Unknown Facility"}
                          </span>
                          <div className={cn(
                            "inline-flex w-fit px-2 py-0.5 rounded text-[11px] mt-1",
                            addOn.type === FacilityAddOnType.Inclusive
                              ? "bg-green-100 text-green-800 border border-green-200"
                              : "hidden"
                          )}>
                            {addOn.type === FacilityAddOnType.Inclusive ? "Included" : ""}
                          </div>
                        </div>
                      </div>
                      {canRemove && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Include</span>
                          <button
                            type="button"
                            onClick={() => {
                              console.log("Toggle clicked:", {
                                addOnId: addOn.id,
                                isSelected: selected,
                                currentSelectedIds: scheduleAddOns
                              });
                              if (selected) {
                                handleRemoveAddOn(addOn.id!);
                              } else {
                                const addOnFacility = addOnFacilities?.data?.find(f => f.id === addOn.add_on_facility_id);
                                const newScheduleAddOns = [...scheduleAddOns, {
                                  id: addOn.id!,
                                  max_attendees: 1,
                                  price: addOn.price || 0,
                                  name: addOnFacility?.name || "",
                                  type: addOn.type,
                                  add_on_facility_id: addOn.add_on_facility_id!,
                                }];
                                console.log("Setting new schedule add-ons:", newScheduleAddOns);
                                setValue("add_ons", newScheduleAddOns);
                                if (trigger) {
                                  trigger("add_ons");
                                }
                              }
                            }}
                            className={cn(
                              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                              selected ? "bg-primary" : "bg-gray-300"
                            )}
                            aria-pressed={selected}
                            aria-label="Toggle include add-on"
                          >
                            <span
                              className={cn(
                                "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
                                selected ? "translate-x-5" : "translate-x-1"
                              )}
                            />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {/* Facility Name (read-only) */}
                      <div className="space-y-1">
                        <Label className="text-sm font-medium">Facility</Label>
                        <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                          {addOnFacilities?.data?.find((f) => f.id === addOn.add_on_facility_id)?.name || "Unknown Facility"}
                        </div>
                        <span className="text-xs text-muted-foreground">Non-editable</span>
                      </div>

                      {/* Price */}
                      <div className="space-y-1">
                        <Label className="text-sm font-medium">Price</Label>
                        <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                          {formatToPKR(addOn.price || 0)}
                        </div>
                        <span className="text-xs text-muted-foreground">Read-only</span>
                      </div>

                      {/* Max Attendees - Only if facility has check-in */}
                      {facilityDetails?.bookable && addOnFacilities?.data?.find((f) => f.id === addOn.add_on_facility_id)?.access_control && selected && (
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">
                            Max Attendees <span className="text-red-500">*</span>
                          </Label>
                          <Controller
                            name={`add_ons.${selectedIndex}.max_attendees`}
                            control={control}
                            defaultValue={1}
                            rules={{
                              required: selected ? "Required" : false,
                              min: { value: 1, message: "Must be at least 1" },
                              validate: (value) => {
                                const addOnFacility = addOnFacilities?.data?.find(f => f.id === addOn.add_on_facility_id);
                                if (addOnFacility?.max_capacity && value && value > addOnFacility.max_capacity) {
                                  return `Max Attendees cannot exceed the facility capacity of ${addOnFacility.max_capacity}.`;
                                }
                                return true;
                              }
                            }}
                            render={({ field }) => (
                              <Input
                                type="number"
                                placeholder="Enter max attendees"
                                disabled={!selected}
                                {...field}
                                className={cn(
                                  !selected && "bg-gray-100 text-gray-500"
                                )}
                              />
                            )}
                          />
                          {errors?.add_ons?.[selectedIndex]?.max_attendees && (
                            <span className="text-red-500 text-xs">
                              {errors.add_ons?.[selectedIndex]?.max_attendees?.message}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Type */}
                      <div className="space-y-1">
                        <Label className="text-sm font-medium">Type</Label>
                        <div className={cn(
                          "p-2 rounded border text-sm font-medium",
                          addOn.type === FacilityAddOnType.Inclusive
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-blue-100 text-blue-800 border-blue-200"
                        )}>
                          {addOn.type === FacilityAddOnType.Inclusive ? "Inclusive" : "Exclusive"}
                        </div>
                        <span className="text-xs text-muted-foreground">Non-editable</span>
                      </div>

                      {/* Description */}
                      {addOn.description && (
                        <div className="space-y-1 col-span-full">
                          <Label className="text-sm font-medium">Description</Label>
                          <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                            {addOn.description}
                          </div>
                        </div>
                      )}

                      {/* Images - sourced from the selected add-on facility */}
                      {(() => {
                        const selectedFacility = addOnFacilities?.data?.find((f) => f.id === addOn.add_on_facility_id);
                        const images: string[] = Array.isArray(selectedFacility?.image_url) ? (selectedFacility?.image_url as string[]) : [];
                        const openLightbox = (startIndex: number) => {
                          setGallery({ open: true, images, index: startIndex });
                        };
                        return (
                          <div className="space-y-1 col-span-full">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-medium">Images</Label>
                              <div className="flex items-center gap-3">
                                <span className="text-[11px] text-gray-500">{images.length} {images.length === 1 ? "image" : "images"}</span>
                                {images.length > 0 && (
                                  <Button type="button" variant="outline" size="sm" onClick={() => openLightbox(0)}>
                                    View Gallery
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 h-[96px] w-full border bg-background rounded-md bg-gray-100 overflow-x-auto px-2">
                              {isLoadingAddOnFacilities ? (
                                <>
                                  {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="h-[72px] w-[72px] rounded bg-gray-200 animate-pulse" />
                                  ))}
                                </>
                              ) : images.length > 0 ? (
                                (() => {
                                  const preview = images.slice(0, 5);
                                  const remaining = images.length - preview.length;
                                  return (
                                    <>
                                      {preview.map((imgUrl, i) => (
                                        <button
                                          key={i}
                                          type="button"
                                          onClick={() => openLightbox(i)}
                                          className="relative h-[72px] w-[72px] shrink-0 rounded overflow-hidden border hover:ring-2 hover:ring-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/80"
                                          aria-label={`View image ${i + 1}`}
                                        >
                                          <img
                                            src={`${VITE_VIEW_S3_URL}/${imgUrl}`}
                                            alt={`Add-on ${i + 1}`}
                                            className="h-full w-full object-cover"
                                          />
                                          {i === preview.length - 1 && remaining > 0 && (
                                            <div className="absolute inset-0 bg-black/50 text-white flex items-center justify-center text-sm">
                                              +{remaining}
                                            </div>
                                          )}
                                        </button>
                                      ))}
                                    </>
                                  );
                                })()
                              ) : (
                                <div className="flex items-center justify-center w-full">
                                  <p className="text-xs text-gray-500">No images for selected add-on facility</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </Card>

      {gallery.open && (
        <div className="fixed inset-0 z-[60] top-0">
          <div
            className="absolute inset-0 -top-4 bg-black/70"
            onClick={() => setGallery({ open: false, images: [], index: 0 })}
          />
          <div className="absolute inset-0 flex items-center justify-center ">
            <div className="relative max-w-[90vw] min-h-[45vh] max-h-[85vh] w-full flex items-center justify-center ">
              <button
                type="button"
                className="absolute top-2 right-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                aria-label="Close gallery"
                onClick={() => setGallery({ open: false, images: [], index: 0 })}
              >
                <i className="fa fa-xmark" />
              </button>

              {gallery.images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="absolute left-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                    aria-label="Previous image"
                    onClick={() =>
                      setGallery((g) => ({
                        ...g,
                        index: (g.index - 1 + g.images.length) % g.images.length,
                      }))
                    }
                  >
                    <i className="fa fa-chevron-left" />
                  </button>
                  <button
                    type="button"
                    className="absolute right-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                    aria-label="Next image"
                    onClick={() =>
                      setGallery((g) => ({
                        ...g,
                        index: (g.index + 1) % g.images.length,
                      }))
                    }
                  >
                    <i className="fa fa-chevron-right" />
                  </button>
                </>
              )}

              <img
                src={`${VITE_VIEW_S3_URL}/${gallery.images[gallery.index]}`}
                alt={`Gallery image ${gallery.index + 1}`}
                className="max-h-[85vh] max-w-[90vw] object-contain rounded shadow-lg"
              />
              {gallery.images.length > 0 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-white bg-black/50 rounded px-2 py-0.5">
                  {gallery.index + 1} / {gallery.images.length}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingAddOn;